import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  getPartTimersMaster,
  savePartTimerMaster,
  deletePartTimerMaster,
  getAllUsers,
  getPartTimerManualEntries,
  getAllAttendanceRecords,
  BUSINESS_ENTITIES
} from '../firebase/firestore';

const PartTimerMasterManagement = ({ selectedYear, selectedMonth, onChangePeriod }) => {
  const [partTimers, setPartTimers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingPartTimer, setEditingPartTimer] = useState(null);

  // 년/월 선택 (제어 또는 내부 상태)
  const fallbackDate = useMemo(() => new Date(), []);
  const [internalYear, setInternalYear] = useState(fallbackDate.getFullYear());
  const [internalMonth, setInternalMonth] = useState(fallbackDate.getMonth());

  const effectiveYear = typeof selectedYear === 'number' ? selectedYear : internalYear;
  const effectiveMonth = typeof selectedMonth === 'number' ? selectedMonth : internalMonth;

  const updatePeriod = ({ year, month }) => {
    if (onChangePeriod) {
      onChangePeriod({ year, month });
    } else {
      setInternalYear(year);
      setInternalMonth(month);
    }
  };

  const handleYearSelect = (event) => {
    updatePeriod({ year: parseInt(event.target.value, 10), month: effectiveMonth });
  };

  const handleMonthSelect = (event) => {
    updatePeriod({ year: effectiveYear, month: parseInt(event.target.value, 10) });
  };

  const shiftMonth = (offset) => {
    const base = new Date(effectiveYear, effectiveMonth + offset, 1);
    updatePeriod({ year: base.getFullYear(), month: base.getMonth() });
  };

  // 급여 데이터
  const [salaryData, setSalaryData] = useState({});

  const [formData, setFormData] = useState({
    partTimerId: '',
    name: '',
    businessEntity: BUSINESS_ENTITIES[0].id, // 기본값: 첫 번째 사업자
    hourlyWage: '',
    ssn: '',
    address: '',
    phone: '',
    email: '',
    bankName: '',
    accountNumber: ''
  });

  const fetchPartTimers = useCallback(async () => {
    setLoading(true);

    // 1. partTimersMaster 컬렉션에서 데이터 가져오기
    const masterResult = await getPartTimersMaster();
    const masterPartTimers = masterResult.success
      ? masterResult.partTimers.map(pt => ({
          ...pt,
          source: 'master',
          hourlyWage: Number(pt.hourlyWage) || 0,
          isActive: pt.isActive !== false
        }))
      : [];

    // 2. users 컬렉션에서 기존 파트타이머 데이터 가져오기
    const usersResult = await getAllUsers();
    const usersPartTimers = usersResult.success
      ? usersResult.users
          .filter(user => !user.isAdmin && user.employeeType !== 'regular')
          .map(user => ({
            id: user.id,
            name: user.name,
            businessEntity: user.businessEntity,
            hourlyWage: Number(user.hourlyWage) || 0,
            ssn: user.ssn,
            address: user.address,
            phone: user.phone,
            email: user.email,
            bankName: user.bankName,
            accountNumber: user.accountNumber,
            source: 'users',
            isActive: true // 기본적으로 활성화 상태로 간주
          }))
      : [];

    // 3. 두 데이터를 병합 (중복 제거: 같은 id가 있으면 master 우선)
    const masterIds = new Set(masterPartTimers.map(pt => pt.id));
    const combinedPartTimers = [
      ...masterPartTimers,
      ...usersPartTimers.filter(pt => !masterIds.has(pt.id))
    ];

    console.log('파트타이머 목록:', combinedPartTimers.map(pt => pt.name));
    setPartTimers(combinedPartTimers);
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchPartTimers();
  }, [fetchPartTimers]);

  const fetchSalaryData = useCallback(async () => {
    // 급여 데이터 수집
    const allSalaryData = {};

    console.log('급여 데이터 조회 시작:', {
      year: effectiveYear,
      month: effectiveMonth,
      displayMonth: `${effectiveYear}년 ${effectiveMonth + 1}월`
    });

    // 1. 수기 입력 데이터 조회
    for (const entity of BUSINESS_ENTITIES) {
      const result = await getPartTimerManualEntries(
        effectiveYear,
        effectiveMonth + 1,
        entity.id
      );

      if (result.success && result.entries) {
        result.entries.forEach(entry => {
          allSalaryData[entry.name] = entry;
        });
      }
    }

    console.log('수기 입력 급여:', allSalaryData);

    // 2. 출퇴근 기록 기반 급여 계산
    const attendanceResult = await getAllAttendanceRecords();

    if (attendanceResult.success && attendanceResult.records) {
      console.log('출퇴근 기록 총 개수:', attendanceResult.records.length);

      // 파트타이머별로 그룹화
      const partTimerRecords = {};

      attendanceResult.records.forEach(record => {
        const recordDate = new Date(record.date);
        const recordMonth = recordDate.getMonth();
        const recordYear = recordDate.getFullYear();

        // 선택한 년/월과 일치하는지 확인
        if (recordYear === effectiveYear && recordMonth === effectiveMonth) {
          const partTimer = partTimers.find(pt => pt.id === record.userId);

          if (partTimer && partTimer.employeeType !== 'regular') {
            if (!partTimerRecords[partTimer.name]) {
              partTimerRecords[partTimer.name] = {
                name: partTimer.name,
                records: [],
                hourlyWage: partTimer.hourlyWage || 0
              };
            }
            partTimerRecords[partTimer.name].records.push(record);
          }
        }
      });

      console.log('파트타이머별 출퇴근 기록:', partTimerRecords);

      // 각 파트타이머별 급여 계산
      Object.values(partTimerRecords).forEach(data => {
        // 이미 수기 입력 데이터가 있으면 스킵
        if (allSalaryData[data.name]) {
          return;
        }

        // 총 근무시간 및 공휴일 근무시간 계산
        let regularHours = 0;
        let holidayHours = 0;

        data.records.forEach(record => {
          const hours = record.workHours || 0;
          if (record.isHoliday) {
            holidayHours += hours;
          } else {
            regularHours += hours;
          }
        });

        const totalHours = regularHours + holidayHours;

        // 주휴수당 계산
        const weeklyData = {};
        data.records.forEach(record => {
          const date = new Date(record.date);
          const dayOfWeek = date.getDay();
          const weekStart = new Date(date);
          weekStart.setDate(date.getDate() - dayOfWeek);
          const weekKey = weekStart.toISOString().split('T')[0];

          if (!weeklyData[weekKey]) {
            weeklyData[weekKey] = { hours: 0 };
          }
          weeklyData[weekKey].hours += record.workHours || 0;
        });

        let weeklyAllowanceHours = 0;
        Object.values(weeklyData).forEach(week => {
          if (week.hours >= 15) {
            const proportionalHours = (week.hours / 40) * 8;
            weeklyAllowanceHours += Math.min(proportionalHours, 8);
          }
        });

        // 급여 계산
        const hourlyWage = data.hourlyWage;
        // 일반 근무: 시급 × 시간
        const regularPay = Math.floor(regularHours * hourlyWage);
        // 공휴일 근무: 시급 × 1.5 × 시간
        const holidayPay = Math.floor(holidayHours * hourlyWage * 1.5);
        const basicPay = regularPay + holidayPay;
        const weeklyAllowance = Math.floor(weeklyAllowanceHours * hourlyWage);
        const totalPay = basicPay + weeklyAllowance;
        const businessTax = Math.floor(totalPay * 0.033);
        const netPay = totalPay - businessTax;

        allSalaryData[data.name] = {
          name: data.name,
          workHours: totalHours,
          regularHours: regularHours,
          holidayHours: holidayHours,
          hourlyWage: hourlyWage,
          basicPay: basicPay,
          weeklyAllowance: weeklyAllowance,
          totalPay: totalPay,
          businessTax: businessTax,
          netPay: netPay,
          source: 'attendance'
        };
      });
    }

    console.log('최종 급여 데이터 (수기 + 출퇴근):', allSalaryData);
    setSalaryData(allSalaryData);
  }, [effectiveMonth, effectiveYear, partTimers]);

  useEffect(() => {
    if (partTimers.length > 0) {
      fetchSalaryData();
    }
  }, [fetchSalaryData, partTimers.length]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const partTimerData = {
      ...formData,
      hourlyWage: Number(formData.hourlyWage),
      isActive: true
    };

    const partTimerId = formData.partTimerId || `PT${Date.now()}`;
    const result = await savePartTimerMaster(partTimerId, partTimerData);

    if (result.success) {
      alert(editingPartTimer ? '파트타이머 정보가 수정되었습니다.' : '파트타이머가 등록되었습니다.');
      setShowAddForm(false);
      setEditingPartTimer(null);
      setFormData({
        partTimerId: '',
        name: '',
        businessEntity: BUSINESS_ENTITIES[0].id,
        hourlyWage: '',
        ssn: '',
        address: '',
        phone: '',
        email: '',
        bankName: '',
        accountNumber: ''
      });
      fetchPartTimers();
    } else {
      alert('저장 실패: ' + result.error);
    }
  };

  const handleEdit = (partTimer) => {
    setEditingPartTimer(partTimer);
    setFormData({
      partTimerId: partTimer.id,
      name: partTimer.name || '',
      businessEntity: partTimer.businessEntity || BUSINESS_ENTITIES[0].id,
      hourlyWage: partTimer.hourlyWage || '',
      ssn: partTimer.ssn || '',
      address: partTimer.address || '',
      phone: partTimer.phone || '',
      email: partTimer.email || '',
      bankName: partTimer.bankName || '',
      accountNumber: partTimer.accountNumber || ''
    });
    setShowAddForm(true);
  };

  const handleDelete = async (partTimerId) => {
    if (window.confirm('정말로 이 파트타이머를 삭제하시겠습니까?')) {
      const result = await deletePartTimerMaster(partTimerId);
      if (result.success) {
        alert('파트타이머가 삭제되었습니다.');
        fetchPartTimers();
      } else {
        alert('삭제 실패: ' + result.error);
      }
    }
  };

  const handleToggleMasterStatus = async (partTimer) => {
    const currentlyActive = partTimer.isActive !== false;
    const message = currentlyActive
      ? `${partTimer.name}의 마스터 등록을 해지하시겠습니까?\n(세무 보기에서 제외됩니다)`
      : `${partTimer.name}을(를) 다시 마스터로 등록하시겠습니까?`;

    if (!window.confirm(message)) {
      return;
    }

    const payload = {
      name: partTimer.name || '',
      businessEntity: partTimer.businessEntity || BUSINESS_ENTITIES[0].id,
      hourlyWage: Number(partTimer.hourlyWage) || 0,
      ssn: partTimer.ssn || '',
      address: partTimer.address || '',
      phone: partTimer.phone || '',
      email: partTimer.email || '',
      bankName: partTimer.bankName || '',
      accountNumber: partTimer.accountNumber || '',
      isActive: !currentlyActive
    };

    const result = await savePartTimerMaster(partTimer.id, payload);
    if (result.success) {
      alert(currentlyActive ? '마스터 등록이 해지되었습니다.' : '마스터가 재등록되었습니다.');
      fetchPartTimers();
    } else {
      alert('상태 변경 실패: ' + result.error);
    }
  };

  const handleMigrateToMaster = async (partTimer) => {
    if (window.confirm(`${partTimer.name}을(를) 마스터 데이터로 등록하시겠습니까?`)) {
      const partTimerData = {
        name: partTimer.name,
        businessEntity: partTimer.businessEntity || BUSINESS_ENTITIES[0].id,
        hourlyWage: partTimer.hourlyWage || 0,
        ssn: partTimer.ssn || '',
        address: partTimer.address || '',
        phone: partTimer.phone || '',
        email: partTimer.email || '',
        bankName: partTimer.bankName || '',
        accountNumber: partTimer.accountNumber || '',
        isActive: true
      };

      const result = await savePartTimerMaster(partTimer.id, partTimerData);
      if (result.success) {
        alert('마스터 데이터로 등록되었습니다.');
        fetchPartTimers();
      } else {
        alert('등록 실패: ' + result.error);
      }
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('ko-KR').format(amount);
  };

  return (
    <div className="container mx-auto p-4">
      <div className="bg-white rounded-lg shadow p-6">
        <div className="mb-6">
          <div className="flex justify-between items-center mb-2">
            <h2 className="text-2xl font-bold">파트타이머 마스터 관리</h2>
            <div className="flex items-center gap-4">
              {/* 년/월 선택 */}
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => shiftMonth(-1)}
                  className="px-3 py-2 rounded border border-gray-300 text-sm hover:bg-gray-100"
                >
                  이전달
                </button>
                <select
                  value={effectiveYear}
                  onChange={handleYearSelect}
                  className="px-3 py-2 border rounded"
                >
                  {Array.from({ length: 7 }, (_, idx) => effectiveYear - 3 + idx).map(year => (
                    <option key={year} value={year}>{year}년</option>
                  ))}
                </select>
                <select
                  value={effectiveMonth}
                  onChange={handleMonthSelect}
                  className="px-3 py-2 border rounded"
                >
                  {[...Array(12)].map((_, i) => (
                    <option key={i} value={i}>{i + 1}월</option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => shiftMonth(1)}
                  className="px-3 py-2 rounded border border-gray-300 text-sm hover:bg-gray-100"
                >
                  다음달
                </button>
              </div>
              <button
                onClick={() => setShowAddForm(!showAddForm)}
                className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
              >
                {showAddForm ? '취소' : '파트타이머 추가'}
              </button>
            </div>
          </div>
          {/* 디버깅 정보 */}
          <div className="text-sm text-gray-600 bg-gray-100 p-2 rounded">
            <p>현재 조회: {effectiveYear}년 {effectiveMonth + 1}월 |
               파트타이머: {partTimers.length}명 |
               급여 데이터: {Object.keys(salaryData).length}건
            </p>
            {partTimers.length > 0 && (
              <p className="text-xs mt-1">
                파트타이머 이름: {partTimers.map(pt => pt.name).join(', ')}
              </p>
            )}
            {Object.keys(salaryData).length > 0 && (
              <p className="text-xs mt-1">
                급여 입력된 이름: {Object.keys(salaryData).join(', ')}
              </p>
            )}
          </div>
        </div>

        {showAddForm && (
          <div className="mb-6 p-4 border rounded">
            <h3 className="text-lg font-bold mb-4">
              {editingPartTimer ? '파트타이머 정보 수정' : '신규 파트타이머 등록'}
            </h3>
            <form onSubmit={handleSubmit} className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">이름 *</label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full px-3 py-2 border rounded"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">사업자 *</label>
                <select
                  required
                  value={formData.businessEntity}
                  onChange={(e) => setFormData({...formData, businessEntity: e.target.value})}
                  className="w-full px-3 py-2 border rounded"
                >
                  {BUSINESS_ENTITIES.map(entity => (
                    <option key={entity.id} value={entity.id}>
                      {entity.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">시급 *</label>
                <input
                  type="number"
                  required
                  value={formData.hourlyWage}
                  onChange={(e) => setFormData({...formData, hourlyWage: e.target.value})}
                  className="w-full px-3 py-2 border rounded"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">주민등록번호</label>
                <input
                  type="text"
                  value={formData.ssn}
                  onChange={(e) => setFormData({...formData, ssn: e.target.value})}
                  placeholder="숫자만 입력 (예: 9001011234567)"
                  maxLength="13"
                  className="w-full px-3 py-2 border rounded"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">전화번호</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  className="w-full px-3 py-2 border rounded"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">이메일</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="w-full px-3 py-2 border rounded"
                />
              </div>

              <div className="col-span-2">
                <label className="block text-sm font-medium mb-1">주소</label>
                <input
                  type="text"
                  value={formData.address}
                  onChange={(e) => setFormData({...formData, address: e.target.value})}
                  className="w-full px-3 py-2 border rounded"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">은행명</label>
                <input
                  type="text"
                  value={formData.bankName}
                  onChange={(e) => setFormData({...formData, bankName: e.target.value})}
                  className="w-full px-3 py-2 border rounded"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">계좌번호</label>
                <input
                  type="text"
                  value={formData.accountNumber}
                  onChange={(e) => setFormData({...formData, accountNumber: e.target.value})}
                  className="w-full px-3 py-2 border rounded"
                />
              </div>

              <div className="col-span-2 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddForm(false);
                    setEditingPartTimer(null);
                    setFormData({
                      partTimerId: '',
                      name: '',
                      businessEntity: BUSINESS_ENTITIES[0].id,
                      hourlyWage: '',
                      ssn: '',
                      address: '',
                      phone: '',
                      email: '',
                      bankName: '',
                      accountNumber: ''
                    });
                  }}
                  className="px-4 py-2 border rounded hover:bg-gray-100"
                >
                  취소
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                >
                  {editingPartTimer ? '수정' : '등록'}
                </button>
              </div>
            </form>
          </div>
        )}

        {loading ? (
          <div className="text-center py-8">
            <div className="spinner-border animate-spin inline-block w-8 h-8 border-4 rounded-full text-blue-600"></div>
            <p className="mt-2">로딩 중...</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full table-auto border-collapse text-sm">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border px-2 py-2">이름</th>
                  <th className="border px-2 py-2">계정 ID</th>
                  <th className="border px-2 py-2">사업자</th>
                  <th className="border px-2 py-2">시급</th>
                  <th className="border px-2 py-2">근무시간<br/>(일반/공휴일)</th>
                  <th className="border px-2 py-2">기본급</th>
                  <th className="border px-2 py-2">주휴수당</th>
                  <th className="border px-2 py-2">총급여</th>
                  <th className="border px-2 py-2">실수령액</th>
                  <th className="border px-2 py-2">전화번호</th>
                  <th className="border px-2 py-2">출처</th>
                  <th className="border px-2 py-2">작업</th>
                </tr>
              </thead>
              <tbody>
                {partTimers.length > 0 ? (
                  partTimers.map((partTimer) => {
                    const salary = salaryData[partTimer.name];
                    const hasSalary = !!salary;

                    return (
                      <tr
                        key={partTimer.id}
                        className={`hover:bg-gray-50 ${
                          partTimer.source === 'users'
                            ? 'bg-yellow-50'
                            : partTimer.isActive === false
                              ? 'bg-red-50'
                              : hasSalary
                                ? 'bg-green-50'
                                : ''
                        }`}
                      >
                        <td className="border px-2 py-2 font-bold">{partTimer.name}</td>
                        <td className="border px-2 py-2 text-xs">
                          {partTimer.email || partTimer.id || '-'}
                        </td>
                        <td className="border px-2 py-2 text-xs">
                          {BUSINESS_ENTITIES.find(e => e.id === partTimer.businessEntity)?.name || '-'}
                        </td>
                        <td className="border px-2 py-2 text-right">
                          ₩{formatCurrency(partTimer.hourlyWage || 0)}
                        </td>
                        <td className="border px-2 py-2 text-center">
                          {hasSalary ? (
                            <div>
                              <div>{salary.workHours}시간</div>
                              {salary.holidayHours > 0 && (
                                <div className="text-xs text-orange-600">
                                  ({salary.regularHours || 0}h / 🎊{salary.holidayHours}h)
                                </div>
                              )}
                            </div>
                          ) : '-'}
                        </td>
                        <td className="border px-2 py-2 text-right">
                          {hasSalary ? (
                            <div>
                              <div>₩{formatCurrency(salary.basicPay)}</div>
                              {salary.holidayHours > 0 && (
                                <div className="text-xs text-orange-600">
                                  (공휴일 1.5배 포함)
                                </div>
                              )}
                            </div>
                          ) : '-'}
                        </td>
                        <td className="border px-2 py-2 text-right text-green-600">
                          {hasSalary ? `₩${formatCurrency(salary.weeklyAllowance || 0)}` : '-'}
                        </td>
                        <td className="border px-2 py-2 text-right font-bold">
                          {hasSalary ? `₩${formatCurrency(salary.totalPay)}` : '-'}
                        </td>
                        <td className="border px-2 py-2 text-right font-bold text-blue-600">
                          {hasSalary ? `₩${formatCurrency(salary.netPay)}` : '-'}
                        </td>
                        <td className="border px-2 py-2 text-xs">{partTimer.phone || '-'}</td>
                        <td className="border px-2 py-2 text-center">
                          {partTimer.source === 'users' ? (
                            <span className="inline-block bg-yellow-200 text-yellow-800 px-1 py-0.5 rounded text-xs">
                              기존
                            </span>
                          ) : partTimer.isActive === false ? (
                            <span className="inline-block bg-gray-300 text-gray-700 px-1 py-0.5 rounded text-xs">
                              해지
                            </span>
                          ) : (
                            <span className="inline-block bg-green-200 text-green-800 px-1 py-0.5 rounded text-xs">
                              마스터
                            </span>
                          )}
                        </td>
                        <td className="border px-2 py-2">
                          {partTimer.source === 'users' ? (
                            <button
                              onClick={() => handleMigrateToMaster(partTimer)}
                              className="bg-blue-500 text-white px-2 py-1 rounded text-xs hover:bg-blue-600 w-full mb-1"
                            >
                              마스터등록
                            </button>
                          ) : (
                            <>
                              <button
                                onClick={() => handleToggleMasterStatus(partTimer)}
                                className={`text-xs w-full mb-1 px-2 py-1 text-white rounded hover:opacity-90 ${
                                  partTimer.isActive === false
                                    ? 'bg-purple-500'
                                    : 'bg-purple-600'
                                }`}
                              >
                                {partTimer.isActive === false ? '마스터 재등록' : '마스터 해지'}
                              </button>
                              <button
                                onClick={() => handleEdit(partTimer)}
                                className="bg-green-500 text-white px-2 py-1 rounded text-xs hover:bg-green-600 w-full mb-1"
                              >
                                수정
                              </button>
                              <button
                                onClick={() => handleDelete(partTimer.id)}
                                className="bg-red-500 text-white px-2 py-1 rounded text-xs hover:bg-red-600 w-full"
                              >
                                삭제
                              </button>
                            </>
                          )}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="12" className="border px-4 py-8 text-center text-gray-500">
                      등록된 파트타이머가 없습니다.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default PartTimerMasterManagement;
